import { rg4js } from '../lib/raygun';
import { defineStore } from 'pinia';
import { auth } from '../lib/firebase';
import { signOut } from 'firebase/auth';
import { apiGet, bootstrap, takeEarlyBootstrap } from '../lib/auth/bootstrap';
import { useOrganizationStore } from './organization';
import EventBus from '@/layouts/slots/event-bus';

// Errors that mean core or Firebase has ended this session.
const DEAD_SESSION_CODES = new Set(['ERR_UNAUTHORIZED', 'auth/user-token-expired', 'auth/user-disabled', 'auth/user-not-found']);
const isDeadSession = (e) => e?.statusCode === 401 || DEAD_SESSION_CODES.has(e?.code);
// API errors the shared client reports app-wide (see nucleus/api.js).
const EMITTED_CODES = new Set(['ERR_FORBIDDEN', 'ERR_PROVIDER_MISMATCH', 'ERR_NOT_FOUND', 'ERR_UNAUTHORIZED']);

// Profile load in flight: { uid, promise }.
let inflight = {};

export const useUserStore = defineStore('user', {
    state: () => ({
        currentUser: null, // Set
        loggedIn: false, // Set
        accessToken: null,
        organization: null,
        displayName: null, // Set
        picture: null, // set
        email: null, // Set
        _id: null, // Set
        organizations: [],
        scaleAdminRole: null,
        eulaAccepted: null
    }),
    actions: {
        async login({ user: firebaseUser }) {
            await this.setProfile(firebaseUser);
        },
        logout() {
            rg4js('trackEvent', 'Logout');
            rg4js('endSession');
            rg4js('setUser', {
                isAnonymous: true,
            });
            this.$reset();
            // The org store is persisted and holds the org the startup batch
            // confirmed; neither may carry over to whoever signs in next in
            // this tab, whether the user logged out or the session was revoked.
            useOrganizationStore().reset();
        },
        // The auth listener and the login page both call this for one sign-in;
        // they share a single load. Takes a Firebase User or UserCredential.
        setProfile(profile) {
            const firebaseUser = profile?.user || profile;
            if (!firebaseUser) {
                return this.logout();
            }
            if (inflight.uid !== firebaseUser.uid) {
                const load = this.loadProfile(firebaseUser).finally(() => {
                    if (inflight.promise === load) inflight = {};
                });
                inflight = { uid: firebaseUser.uid, promise: load };
            }
            return inflight.promise;
        },
        async loadProfile(firebaseUser) {
            this.$reset();
            this.currentUser = firebaseUser;
            this.loggedIn = true;
            this.displayName = firebaseUser.displayName;
            this.email = firebaseUser.email;
            this.picture = firebaseUser.picture || firebaseUser.photoURL;

            // The startup requests (profile, organizations, org in the URL) run
            // together in one batch. A returning visitor's batch may already be
            // in flight with the previous visit's token; it is used when it was
            // made for this same account, and any failure reruns the batch with
            // Firebase's token.
            // Same org the router guard will settle on: the URL's, or the
            // persisted one it falls back to when the URL has none.
            const orgId = new URL(window.location.href).searchParams.get('org') || useOrganizationStore().orgId || null;
            let token;
            let me;
            let orgs;
            let org;
            try {
                let claims;
                ({ token, claims } = await firebaseUser.getIdTokenResult());
                const early = takeEarlyBootstrap(claims.user);
                ({ me, orgs, org } = early
                    ? await early.result.catch(() => bootstrap(token, orgId).result)
                    : await bootstrap(token, orgId).result);

                // Core only rewrites the claims when they changed (a new account
                // gets its "user" claim here); refresh then so the next requests
                // carry them, and fetch the organizations with the new claims.
                // A revoked session surfaces here too: core accepts a token
                // issued just before the revoke, reports the changed grants, and
                // the refresh fails because the refresh token is gone.
                if (me.claimsUpdated || !orgs) {
                    const fresh = me.claimsUpdated ? await auth.currentUser?.getIdToken(true) : token;
                    orgs = await apiGet(fresh ?? token, '/api/v1/users/me/organizations');
                }
            } catch (e) {
                // Revoked session (#4776): sign out and clear the store so the
                // router guard, which keys on currentUser, lands on login.
                if (isDeadSession(e)) {
                    await signOut(auth);
                    this.logout();
                } else {
                    // Same reporting the shared client's interceptor does for
                    // these codes, so the rest of the app reacts as before.
                    rg4js('send', { error: e, message: e?.message });
                    if (EMITTED_CODES.has(e?.code)) EventBus.emit('error', e);
                }
                throw e;
            }

            this._id = me._id;
            this.eulaAccepted = me.eulaAccepted || null;
            this.scaleAdminRole = me.scaleAdminRole || null;

            rg4js('setUser', {
                identifier: this._id,
                isAnonymous: false,
                email: this.email,
                firstName: this.displayName,
                fullName: this.displayName,
            });

            this.setOrganizations(orgs);
            if (org?._id) useOrganizationStore().setValidatedOrganization(org);
        },
        setEulaAccepted(acceptedStatus) {
            this.eulaAccepted = acceptedStatus;
        },
        setOrganizations(organizations) {
            this.organizations = organizations;
        },
    },
    getters: {
        isLoggedIn: state => {
            return state.loggedIn;
        },
        profile: state => {
            return {
                displayName: state.displayName,
                email: state.email,
                picture: state.picture,
                _id: state._id,
                scaleAdminRole: state.scaleAdminRole,
                eulaAccepted: state.eulaAccepted
            }
        },
        orgs: state => {
            return state.organizations;
        }
    },
    persist: true
});
